import React, { useState, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, ArrowRight, Info } from 'lucide-react';
type ServiceType = 'house-clearance' | 'office-removals' | 'garden-waste' | 'commercial-waste' | 'overseas' | 'single-item';
const serviceBaseRates: Record<ServiceType, [number, number]> = {
  'house-clearance': [150, 250],
  'office-removals': [200, 350],
  'garden-waste': [80, 150],
  'commercial-waste': [80, 180],
  overseas: [500, 1000],
  'single-item': [40, 80]
};
const volumeMultipliers: Record<string, number> = {
  '1-5-bags': 1,
  'half-van': 1.5,
  'full-van': 2.5,
  '2-vans': 4,
  '3-plus': 6
};
const accessMultipliers: Record<string, number> = {
  easy: 1,
  moderate: 1.2,
  difficult: 1.5
};
const serviceOptions: {
  key: ServiceType;
  label: string;
  icon: string;
}[] = [{
  key: 'house-clearance',
  label: 'House Clearance',
  icon: '🏠'
}, {
  key: 'office-removals',
  label: 'Office Removals',
  icon: '🏢'
}, {
  key: 'garden-waste',
  label: 'Garden Waste',
  icon: '🌿'
}, {
  key: 'commercial-waste',
  label: 'Commercial Waste',
  icon: '🚛'
}, {
  key: 'overseas',
  label: 'Overseas Move',
  icon: '🌍'
}, {
  key: 'single-item',
  label: 'Single Item',
  icon: '📦'
}];
export function CostCalculatorPage() {
  const [service, setService] = useState<ServiceType | null>(null);
  const [volume, setVolume] = useState('');
  const [floors, setFloors] = useState(1);
  const [access, setAccess] = useState('easy');
  const [sameDay, setSameDay] = useState(false);
  const [step, setStep] = useState(1);
  const calculateEstimate = (): [number, number] | null => {
    if (!service || !volume) return null;
    const [baseMin, baseMax] = serviceBaseRates[service];
    const volMult = volumeMultipliers[volume] || 1;
    const accMult = accessMultipliers[access] || 1;
    const floorMult = 1 + (floors - 1) * 0.1;
    const sameDayMult = sameDay ? 1.25 : 1;
    const min = Math.round(baseMin * volMult * accMult * floorMult * sameDayMult / 10) * 10;
    const max = Math.round(baseMax * volMult * accMult * floorMult * sameDayMult / 10) * 10;
    return [min, max];
  };
  const estimate = calculateEstimate();
  const canCalculate = service && volume;
  return <div className="pt-16 lg:pt-20" data-id="element-474">
      {/* Hero */}
      <section className="py-16 px-4" style={{
      background: 'linear-gradient(135deg, #1B8F4B 0%, #0F5D2E 100%)'
    }} data-id="element-475">
        <div className="max-w-7xl mx-auto text-center" data-id="element-476">
          <Calculator className="w-12 h-12 text-green-300 mx-auto mb-4" data-id="element-477" />
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4" style={{
          fontFamily: 'Montserrat, sans-serif'
        }} data-id="element-478">
            Instant Cost Estimator
          </h1>
          <p className="text-green-100 text-lg max-w-2xl mx-auto" data-id="element-479">
            Get an instant price estimate in under 2 minutes. No obligation, no
            sign-up required.
          </p>
        </div>
      </section>

      <section className="py-16 px-4 bg-gray-50" data-id="element-480">
        <div className="max-w-3xl mx-auto" data-id="element-481">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-482">
            {/* Progress */}
            <div className="px-8 pt-8 pb-4" data-id="element-483">
              <div className="flex items-center gap-2 mb-6" data-id="element-484">
                {[1, 2, 3, 4].map(s => <Fragment key={s} data-id="element-485">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-200" style={{
                  backgroundColor: step >= s ? '#1B8F4B' : '#E5E7EB',
                  color: step >= s ? '#fff' : '#9CA3AF'
                }} data-id="element-486">
                      {s}
                    </div>
                    {s < 4 && <div className="flex-1 h-1 rounded-full" style={{
                  backgroundColor: step > s ? '#1B8F4B' : '#E5E7EB'
                }} data-id="element-487" />}
                  </Fragment>)}
              </div>
            </div>

            <div className="px-8 pb-8 space-y-8" data-id="element-488">
              {/* Step 1: Service */}
              <div data-id="element-489">
                <h2 className="text-lg font-bold text-gray-900 mb-4" style={{
                fontFamily: 'Montserrat, sans-serif'
              }} data-id="element-490">
                  Step 1: What service do you need?
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-id="element-491">
                  {serviceOptions.map(({
                  key,
                  label,
                  icon
                }) => <button key={key} onClick={() => {
                  setService(key);
                  setStep(Math.max(step, 2));
                }} className="p-4 rounded-xl border-2 text-center transition-all duration-200 hover:border-[#1B8F4B]" style={{
                  borderColor: service === key ? '#1B8F4B' : '#E5E7EB',
                  backgroundColor: service === key ? '#F0FDF4' : 'white'
                }} data-id="element-492">
                      <div className="text-2xl mb-1" data-id="element-493">{icon}</div>
                      <p className="text-xs font-semibold text-gray-700" data-id="element-494">
                        {label}
                      </p>
                    </button>)}
                </div>
              </div>

              {/* Step 2: Volume */}
              {step >= 2 && <div data-id="element-495">
                  <h2 className="text-lg font-bold text-gray-900 mb-4" style={{
                fontFamily: 'Montserrat, sans-serif'
              }} data-id="element-496">
                    Step 2: How much waste / how big is the job?
                  </h2>
                  <select value={volume} onChange={e => {
                setVolume(e.target.value);
                if (e.target.value) setStep(Math.max(step, 3));
              }} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-[#1B8F4B] focus:ring-2 focus:ring-[#1B8F4B]/20 transition-colors text-sm bg-white" data-id="element-497">
                    <option value="" data-id="element-498">Select volume...</option>
                    <option value="1-5-bags" data-id="element-499">1–5 bags / small load</option>
                    <option value="half-van" data-id="element-500">Half van load</option>
                    <option value="full-van" data-id="element-501">Full van load</option>
                    <option value="2-vans" data-id="element-502">2 van loads</option>
                    <option value="3-plus" data-id="element-503">3+ van loads / lorry</option>
                  </select>
                </div>}

              {/* Step 3: Access & Floors */}
              {step >= 3 && <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-504">
                  <div data-id="element-505">
                    <h2 className="text-lg font-bold text-gray-900 mb-4" style={{
                  fontFamily: 'Montserrat, sans-serif'
                }} data-id="element-506">
                      Step 3: Property floors
                    </h2>
                    <div className="flex items-center gap-4" data-id="element-507">
                      <button onClick={() => setFloors(Math.max(1, floors - 1))} className="w-10 h-10 rounded-full border-2 font-bold text-lg transition-colors hover:border-[#1B8F4B] hover:text-[#1B8F4B]" style={{
                    borderColor: '#E5E7EB'
                  }} data-id="element-508">
                        −
                      </button>
                      <span className="text-2xl font-bold w-8 text-center" style={{
                    fontFamily: 'Montserrat, sans-serif'
                  }} data-id="element-509">
                        {floors}
                      </span>
                      <button onClick={() => {
                    setFloors(Math.min(10, floors + 1));
                    setStep(Math.max(step, 4));
                  }} className="w-10 h-10 rounded-full border-2 font-bold text-lg transition-colors hover:border-[#1B8F4B] hover:text-[#1B8F4B]" style={{
                    borderColor: '#E5E7EB'
                  }} data-id="element-510">
                        +
                      </button>
                    </div>
                  </div>
                  <div data-id="element-511">
                    <h2 className="text-lg font-bold text-gray-900 mb-4" style={{
                  fontFamily: 'Montserrat, sans-serif'
                }} data-id="element-512">
                      Access difficulty
                    </h2>
                    <div className="space-y-2" data-id="element-513">
                      {[['easy', 'Easy (ground floor / drive access)'], ['moderate', 'Moderate (stairs / narrow access)'], ['difficult', 'Difficult (no lift / tight access)']].map(([val, label]) => <label key={val} className="flex items-center gap-3 cursor-pointer" data-id="element-514">
                          <input type="radio" name="access" value={val} checked={access === val} onChange={e => setAccess(e.target.value)} className="accent-[#1B8F4B]" data-id="element-515" />
                          <span className="text-sm text-gray-700" data-id="element-516">{label}</span>
                        </label>)}
                    </div>
                  </div>
                </div>}

              {/* Step 4: Same day */}
              {step >= 4 && <div data-id="element-517">
                  <h2 className="text-lg font-bold text-gray-900 mb-4" style={{
                fontFamily: 'Montserrat, sans-serif'
              }} data-id="element-518">
                    Step 4: Do you need same-day service?
                  </h2>
                  <div className="flex gap-4" data-id="element-519">
                    {[['yes', 'Yes (+25%)', true], ['no', 'No, flexible dates', false]].map(([key, label, val]) => <button key={String(key)} onClick={() => setSameDay(val as boolean)} className="flex-1 py-3 rounded-xl border-2 text-sm font-semibold transition-all duration-200" style={{
                  borderColor: sameDay === val ? '#1B8F4B' : '#E5E7EB',
                  backgroundColor: sameDay === val ? '#F0FDF4' : 'white',
                  color: sameDay === val ? '#1B8F4B' : '#6B7280'
                }} data-id="element-520">
                        {label as string}
                      </button>)}
                  </div>
                </div>}

              {/* Result */}
              {canCalculate && estimate && <div className="rounded-2xl p-6 text-center" style={{
              background: 'linear-gradient(135deg, #1B8F4B 0%, #0F5D2E 100%)'
            }} data-id="element-521">
                  <p className="text-green-200 text-sm font-semibold mb-2" data-id="element-522">
                    Estimated Price Range
                  </p>
                  <p className="text-5xl font-extrabold text-white mb-2" style={{
                fontFamily: 'Montserrat, sans-serif'
              }} data-id="element-523">
                    £{estimate[0]} – £{estimate[1]}
                  </p>
                  <div className="flex items-center justify-center gap-2 text-green-200 text-xs mb-6" data-id="element-524">
                    <Info className="w-3 h-3" data-id="element-525" />
                    Final quote confirmed after photos or site visit
                  </div>
                  <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-[#1B8F4B] bg-white hover:bg-green-50 transition-colors" style={{
                fontFamily: 'Montserrat, sans-serif'
              }} data-id="element-526">
                    Send Details for Exact Quote{' '}
                    <ArrowRight className="w-4 h-4" data-id="element-527" />
                  </Link>
                </div>}
            </div>
          </div>
        </div>
      </section>
    </div>;
}